import type {ReactElement} from 'react';
import React from 'react';
import {ThumbnailItemSelectionState} from '@Components/thumbnail-item';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Thumbnail} from '@Components/thumbnail';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import {getAssetUrl} from '@Utils/s3.util';
import {ContextualMenuThumbnailItem} from '@Components/contextual-menu/components/contextual-menu-thumbnail-item';
import {getCompatibleVideoFileExtension} from '@Utils/video.util';
import type {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import styles from './base-shadow-thumbnail-options.module.scss';
import {ShadowType} from '@PosterWhiteboard/poster/poster-backend.types';

interface BaseShadowThumbnailOptionsProps {
  selectedType: ShadowType;
  onToggleShadow(newShadowType: ShadowType | null): void;
  itemType: ITEM_TYPE;
  isForText?: boolean;
  isForVector?: boolean;
}

const SHADOW_OPTIONS = [ShadowType.NONE, ShadowType.LIGHT, ShadowType.STRONG, ShadowType.CUSTOM];
const TEXT_THUMBNAIL_BASE_URL = 'poster-editor-thumbnails/text-shadow/';
const MEDIA_THUMBNAIL_BASE_URL = 'poster-editor-thumbnails/image-shadow/';

export function BaseShadowThumbnailOptions({isForText = true, isForVector = false, ...props}: BaseShadowThumbnailOptionsProps): ReactElement<any> | null {
  const getShadowText = (text: ShadowType): string => {
    switch (text) {
      case ShadowType.CUSTOM:
        return window.i18next.t('pmwjs_custom');
      case ShadowType.LIGHT:
        return window.i18next.t('pmwjs_light');
      case ShadowType.STRONG:
        return window.i18next.t('pmwjs_strong');
      case ShadowType.NONE:
      default:
        return window.i18next.t('pmwjs_none');
    }
  };

  const getThumbnail = (type: ShadowType): string => {
    switch (type) {
      case ShadowType.CUSTOM:
        return 'custom';
      case ShadowType.LIGHT:
        return 'light';
      case ShadowType.STRONG:
        return 'strong';
      default:
        return 'none';
    }
  };

  const getThumbClassName = (): string => {
    if (isForText) {
      return styles.thumb;
    }

    return '';
  };

  const getShadowClasses = (type: ShadowType): string => {
    switch (type) {
      case ShadowType.LIGHT:
        return styles.lightShadow;
      case ShadowType.STRONG:
        return styles.strongShadow;
      case ShadowType.CUSTOM:
        return styles.customShadow;
      default:
        return '';
    }
  };

  const getVectorThumbnails = (type: ShadowType): ReactElement<any> => {
    return (
      <div className="flex-center">
        <div className={`${styles.vectorThumbnail} radius-round ${getShadowClasses(type)}`} />
      </div>
    );
  };

  const getThumbnailFromType = (type: ShadowType): ReactElement<any> => {
    if (type === ShadowType.NONE && (isForText || isForVector)) {
      return <Icon icon="icon-ban" size={IconSize.SIZE_ICON_32} />;
    }

    if (isForVector) {
      return getVectorThumbnails(type);
    }

    return (
      <Thumbnail
        className={type === ShadowType.NONE ? styles.none : getThumbClassName()}
        data={{
          type: ThumbnailType.IMAGE,
          imageSrc: getAssetUrl(`${isForText ? TEXT_THUMBNAIL_BASE_URL : MEDIA_THUMBNAIL_BASE_URL}${getThumbnail(type)}.${getCompatibleVideoFileExtension('webp')}`),
        }}
      />
    );
  };

  const prepareThumbnails = (): ReactElement<any>[] => {
    return SHADOW_OPTIONS.map((type) => {
      return (
        <ContextualMenuThumbnailItem
          key={type}
          className={!(isForText || isForVector) ? styles.thumbnailsClasses : ''}
          thumbnailContainerClassName={isForText || isForVector ? '' : `${styles.thumbnail} ${type === ShadowType.NONE ? styles.none : ''} `}
          thumbnail={getThumbnailFromType(type)}
          isSelected={props.selectedType === type}
          selectionState={isForText || isForVector ? ThumbnailItemSelectionState.BORDER : ThumbnailItemSelectionState.OUTLINE}
          text={getShadowText(type)}
          onClick={(): void => {
            props.onToggleShadow(type);
          }}
          hasBorder={isForText || isForVector}
        />
      );
    });
  };

  return <ul className="flex-h-row spacing-m-0">{prepareThumbnails()}</ul>;
}
